import { AxiosError, AxiosResponse, CanceledError } from 'axios';
import { isUndefined } from 'lodash-es';

import * as Sentry from '@sentry/react';
import logger from '@shared/utils/error/logger';
import ToastV2 from '@shared/components/base/BaseToast';
import { SENTRY_ENABLE } from '@shared/env';
import { LOGGER_SPACIAL_SYMBOL } from '@shared/utils/error/config';
import { setRequestCache } from '../cache';
import {
	BUSINESS_ERROR_CODE_MAP_MESSAGE,
	NOT_LOGIN_ERROR_CODE,
	SERVICE_ERROR_CODE_MAP_MESSAGE,
} from '../errorCode';
import { HttpDependency } from '../dependency';
import { filterDataByFormVersion } from '../utils/workflow';
import { IRequestParams } from '../interface';

const resErrorCodeHandle = (response: AxiosResponse) => {
	const { data } = response;

	if (!data) {
		// 空响应体不做处理
		return;
	}

	if (data?.code === NOT_LOGIN_ERROR_CODE) {
		HttpDependency.clearUserInfo();
	}

	const serviceError = SERVICE_ERROR_CODE_MAP_MESSAGE[data?.code];
	if (serviceError) {
		const error = new Error(data.msg, {
			cause: {
				code: data.code,
				message: serviceError,
			},
		});
		logger.error(
			LOGGER_SPACIAL_SYMBOL.GLOBAL,
			`SERVICE_ERROR_[${serviceError || data.msg}]`,
			error,
		);
		return error;
	}

	const businessError = BUSINESS_ERROR_CODE_MAP_MESSAGE[data?.code];
	if (businessError) {
		const error = Error(data.msg, {
			cause: {
				code: data.code,
				message: businessError,
			},
		});

		logger.error(
			LOGGER_SPACIAL_SYMBOL.BUSINESS_ERROR,
			`SERVICE_ERROR_[${serviceError || data.msg}]`,
			error,
		);
		return error;
	}

	return new Error(data.msg, {
		cause: {
			code: data.code,
			message: data.msg,
		},
	});
};

/**
 * 请求成功拦截
 */
export const resFulfillIntcpt = (response: AxiosResponse) => {
	const { status, data, config, headers } = response;
	const traceId = headers['x-request-id'];

	if (traceId && SENTRY_ENABLE) {
		Sentry.setTags({
			traceId: traceId,
		});
	}

	switch (status) {
		case 200:
		case 201: {
			if (resConventionCodeHandle(data)) {
				break;
			}
			if (data.code !== 0 && data.code !== 324004) {
				resErrorCodeHandle(response);
			}

			if (data.code === 0 && (config as IRequestParams['config'])?.cache) {
				const { url = '', params } = response.config;
				setRequestCache({ url: url, data: params }, data);
			}

			if (data.code === 0) {
				HttpDependency.nonBusinessEventTracker(response);
				// form-v2-pre 兼容性
				filterDataByFormVersion(response);
			}

			if (data.code === 520001) {
				// TODO： 服务繁忙（超限），后续有需要可以统一处理
			}

			return data;
		}
	}

	if (isUndefined(data.code) || data.code === 0) {
		return data;
	}

	throw resErrorCodeHandle(response);
};

/**
 * 请求失败拦截
 */
export const resRejectIntcpt = (error: AxiosError) => {
	const response = error.response;
	if (error instanceof CanceledError) {
		console.log('取消请求', error);
		return Promise.reject(error);
	} else if (!isUndefined(response?.status)) {
		logger.error(
			LOGGER_SPACIAL_SYMBOL.NETWORK_ERROR,
			`NETWORK_ERROR_${response?.status}`,
			response,
		);
	}

	return Promise.reject(response);
};

/**
 * 约定的公共code toast事件
 */

export const resConventionCodeHandle = (data: any) => {
	if (data.code === 888888) {
		data.msg && ToastV2.info(data.msg);
		return true;
	}
	return false;
};
